import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { computeCategoryAverage, computeCategoryCoverage } from "../../pages/KPIDashboardPage";
import { formatRoleLabel } from "../../lib/roleLabel";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandShortcut } from "../ui/command";
import ComplianceBanner from "../ComplianceBanner";
import NotificationBell from "../NotificationBell";
import AIDraftsBell from "../AIDraftsBell";
import { TrialBanner } from "../trial/TrialBanner";
import { TrialSoftWall } from "../trial/TrialSoftWall";
import { OnboardingChecklist } from "../trial/OnboardingChecklist";
import GlobalAISearch from "../GlobalAISearch";
import { getDemoChecklistProgress } from "../DemoWelcomeOverlay";
import DemoWelcomeOverlay from "../DemoWelcomeOverlay";
import GuidedWorkflowPanel from "../dashboard/GuidedWorkflowPanel";
import BrandText from "../BrandText";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
} from "../ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import {
  LayoutDashboard,
  FileSearch,
  AlertTriangle,
  FileText,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  User,
  Building2,
  Home,
  ShieldCheck,
  ClipboardCheck,
  BookOpen,
  Library,
  Shield,
  Users,
  BarChart3,
  Plus,
  Calculator,
  Scale,
  ScrollText,
  Target,
  Cpu,
  FileCheck,
  MessageSquare,
  Award,
  GraduationCap,
  Briefcase,
  Activity,
  CircleDot,
  CheckCircle2,
  RefreshCw,
  Lightbulb,
  ClipboardList,
  TrendingUp,
  FileWarning,
  Calendar,
  Siren,
  ShieldAlert,
  Layers,
  Monitor,
  Network,
  Server,
  Sparkles,
  Search,
  Command,
  Clock,
  MapPin,
  PanelLeftClose,
  PanelLeft,
  PlayCircle,
  ListChecks,
  Loader2,
  Database,
  Zap,
  Camera,
} from "lucide-react";

// Wizard step counts for sidebar badges (demo mode only)
const WIZARD_STEP_MAP = {
  "/asset-inventory": 5,
  "/risk-assessment": 5,
  "/recovery-plans": 5,
  "/exercises": 4,
  "/document-management": 3,
  "/crisis-communication": 4,
  "/stakeholder-register": 3,
  "/internal-audit": 4,
  "/recovery-strategy": 3,
  "/emergency-response": 12,
  "/governance": 4,
  "/training-awareness": 3,
  "/lessons-learned": 3,
  "/improvement-actions": 3,
  "/operational-bia": 6,
  "/threat-profiling": 4,
  "/exercise-test-review": 3,
};

// ISO 22301:2019 Clause Descriptions for tooltips
const ISO_CLAUSE_DESCRIPTIONS = {
  "4.1": "Understanding the organization and its context",
  "4.2": "Understanding the needs and expectations of interested parties",
  "4.3": "Determining the scope of the BCMS",
  "4.4": "Business continuity management system",
  "5.1": "Leadership and commitment",
  "5.2": "Policy - Establishing the business continuity policy",
  "5.3": "Organizational roles, responsibilities and authorities",
  "6.1": "Actions to address risks and opportunities",
  "6.2": "Business continuity objectives and planning",
  "6.3": "Planning changes to the BCMS",
  "7.1": "Resources - Determining and providing resources",
  "7.2": "Competence - Training and awareness",
  "7.3": "Awareness",
  "7.4": "Communication - Internal and external",
  "7.5": "Documented information",
  "8.1": "Operational planning and control",
  "8.2.1": "Business impact analysis - Requirements",
  "8.2.2": "Business impact analysis - Conducting BIA",
  "8.2.3": "Risk assessment - Identifying and evaluating risks",
  "8.3": "Business continuity strategies and solutions",
  "8.4": "Business continuity plans and procedures",
  "8.5": "Exercise programme",
  "8.6": "Evaluation of BC documentation and capabilities",
  "9.1": "Monitoring, measurement, analysis and evaluation",
  "9.2": "Internal audit",
  "9.3": "Management review",
  "10.1": "Nonconformity and corrective action",
  "10.2": "Continual improvement",
};

// Helper to get clause color based on PDCA phase
const getClauseColor = (clause) => {
  if (!clause) return { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-300" };
  const num = parseFloat(clause);
  if (num >= 4 && num < 7) return { bg: "bg-blue-100", text: "text-blue-700", border: "border-blue-300" }; // PLAN
  if (num >= 7 && num < 9) return { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-300" }; // DO
  if (num >= 9 && num < 10) return { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-300" }; // CHECK
  if (num >= 10) return { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-300" }; // ACT
  return { bg: "bg-slate-100", text: "text-slate-600", border: "border-slate-300" };
};

// Helper to find current page clause info from navigation data
const getPageClauseInfo = (pathname) => {
  // Check dashboard
  if (pathname === "/dashboard") return null;
  
  // Check organization items
  const orgItems = pdcaNavigation.organization?.items || [];
  for (const item of orgItems) {
    if (item.href === pathname && item.clause) {
      return { clause: item.clause, phase: "ORGANIZATION", phaseColor: "slate", description: ISO_CLAUSE_DESCRIPTIONS[item.clause] };
    }
  }
  
  // Check PDCA sections
  for (const section of pdcaNavigation.sections) {
    for (const sub of section.subsections || []) {
      for (const item of sub.items) {
        if (item.href === pathname && item.clause) {
          const colors = { plan: "blue", do: "emerald", check: "amber", act: "purple" };
          return {
            clause: item.clause,
            phase: section.name,
            phaseColor: colors[section.id] || "slate",
            description: ISO_CLAUSE_DESCRIPTIONS[item.clause],
            sectionDesc: section.description,
            isoClause: section.isoClause,
          };
        }
      }
    }
  }
  
  // Check non-PDCA buckets
  const allSections = [pdcaNavigation.intelligenceAnalytics, pdcaNavigation.platformOperations, pdcaNavigation.account].filter(Boolean);
  for (const sec of allSections) {
    for (const item of sec.items || []) {
      if (item.href === pathname && item.clause) {
        return { clause: item.clause, phase: sec.name, phaseColor: "slate", description: ISO_CLAUSE_DESCRIPTIONS[item.clause] };
      }
    }
  }
  return null;
};

// Helper to get badge color based on badge text
const getBadgeStyle = (badge) => {
  switch (badge) {
    case 'AI': return 'bg-purple-100 text-purple-700 border border-purple-200';
    case 'NEW': return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    case 'Combined': return 'bg-cyan-100 text-cyan-700 border border-cyan-200';
    case 'View Only': return 'bg-amber-100 text-amber-700 border border-amber-200';
    default: return 'bg-sky-100 text-sky-700 border border-sky-200';
  }
};

// PDCA Menu Structure - ISO 22301 aligned with Subsections
// ═══════════════════════════════════════════════════════════════════════
// TAXONOMY v2 (2026-02-22) — Solution Architect audit applied:
//   Merges : Policies (Wizard+Library), CAPA (PACA+Improvement Actions),
//            Exercise Management (+Review tab)
//   Renames: Audit → Assurance, My Tasks → My Workstreams,
//            Reports & Intelligence → Intelligence & Analytics
//   Moves  : Crisis Communication → Emergency Operations,
//            AI Lab → Intelligence & Analytics,
//            Audit Logs / Scheduler / AI Guide Questions → Platform Operations,
//            Create IT-DRP Plan → IT Disaster Recovery
// Deep-link compatibility: legacy routes still resolve; removed-from-nav
// items remain reachable via /bcm-policy, /improvement-actions,
// /exercise-test-review for bookmarks & external links.
// ═══════════════════════════════════════════════════════════════════════
const pdcaNavigation = {
  dashboard: {
    name: "BCM Dashboard",
    href: "/dashboard",
    icon: LayoutDashboard,
    roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"],
  },
  sections: [
    {
      id: "plan",
      name: "PLAN",
      icon: Target,
      color: "bg-blue-600",
      colorLight: "bg-blue-400",
      hoverColor: "hover:bg-blue-700",
      textColor: "text-blue-600",
      bgLight: "bg-blue-50",
      description: "Strategic Intent",
      isoClause: "Clauses 4-6",
      subsections: [
        {
          id: "policy_governance",
          name: "Policy & Governance",
          items: [
            { name: "Policies", href: "/policies", icon: Shield, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "5.2" },
            { name: "Policy Acknowledgment", href: "/policy-acknowledgment", icon: FileCheck, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], clause: "5.2" },
            { name: "Stakeholder Register", href: "/stakeholder-register", icon: Users, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "4.2" },
          ]
        },
        {
          id: "analysis_assessment",
          name: "Analysis & Assessment",
          items: [
            { name: "BIA Configuration", href: "/bia-configuration", icon: Settings, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "8.2.1" },
            { name: "Guided BIA (AI)", href: "/guided-bia", icon: Sparkles, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], clause: "8.2.2", badge: "AI" },
            { name: "Operational BIA", href: "/operational-bia", icon: Cpu, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], clause: "8.2.2" },
            { name: "BIA Report", href: "/bia-report", icon: BarChart3, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "8.2.2" },
            { name: "Threat Profiling", href: "/threat-profiling", icon: AlertTriangle, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "6.1" },
            // ─── Risk Management cluster ───
            { name: "Risk Register", href: "/risk-assessment", icon: AlertTriangle, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], clause: "8.2.3" },
            { name: "My RA Tasks", href: "/ra/my-tasks", icon: ShieldAlert, roles: ["process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], clause: "8.2.3" },
            { name: "Team RA Tracker", href: "/ra/tracker", icon: ClipboardCheck, roles: ["admin", "bcm_manager", "department_head", "process_manager", "app_manager", "it_function_manager"], clause: "8.2.3" },
            { name: "RA Insights (AI)", href: "/ra/dashboard", icon: Zap, roles: ["admin", "bcm_manager", "department_head"], clause: "8.2.3", badge: "AI" },
            { name: "Risk Inheritance", href: "/risk-inheritance", icon: Network, roles: ["admin", "bcm_manager", "department_head"], clause: "8.2.3 / 8.3", badge: "AI" },
          ]
        }
      ]
    },
    {
      id: "do",
      name: "DO",
      icon: Activity,
      color: "bg-emerald-600",
      colorLight: "bg-emerald-400",
      hoverColor: "hover:bg-emerald-700",
      textColor: "text-emerald-600",
      bgLight: "bg-emerald-50",
      description: "Operational Accountability",
      isoClause: "Clauses 7-8",
      subsections: [
        {
          id: "resources_awareness",
          name: "Resources & Awareness",
          items: [
            { name: "Training & Awareness", href: "/training-awareness", icon: GraduationCap, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], clause: "7.2" },
            { name: "Asset Inventory", href: "/asset-inventory", icon: Briefcase, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], clause: "7.1" },
            { name: "Document Management", href: "/document-management", icon: Briefcase, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "7.5" },
          ]
        },
        {
          id: "recovery_planning",
          name: "Recovery Planning",
          items: [
            { name: "Recovery Strategy", href: "/recovery-strategy", icon: Lightbulb, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "8.3" },
            { name: "Recovery Plans", href: "/recovery-plans", icon: FileCheck, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "8.4" },
          ]
        },
        {
          id: "it_disaster_recovery",
          name: "IT Disaster Recovery",
          items: [
            { name: "IT-DRP Command Center", href: "/it-drp-admin", icon: Monitor, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "8.4" },
            { name: "Application Profiles", href: "/application-profiles", icon: Cpu, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "8.4" },
            { name: "Create IT-DRP Plan", href: "/it-drp-wizard", icon: Server, roles: ["process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], clause: "8.4" },
          ]
        },
        {
          id: "emergency_operations",
          name: "Emergency Operations",
          items: [
            { name: "Emergency Response", href: "/emergency-response", icon: Siren, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "8.4.4" },
            { name: "Crisis Communication", href: "/crisis-communication", icon: MessageSquare, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], clause: "7.4" },
            { name: "Orchestration Center", href: "/orchestration-center", icon: Network, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "8.4" },
            { name: "Cyber Recovery Plans", href: "/cyber-recovery", icon: ShieldAlert, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "it_function_manager"], clause: "8.4 / Cyber" },
          ]
        },
        {
          id: "my_workstreams",
          name: "My Workstreams",
          items: [
            { name: "My Work", href: "/my-work", icon: ClipboardList, roles: ["process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor", "admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "8.4", badge: "Unified" },
          ]
        }
      ]
    },
    {
      id: "check",
      name: "CHECK",
      icon: CheckCircle2,
      color: "bg-amber-600",
      colorLight: "bg-amber-400",
      hoverColor: "hover:bg-amber-700",
      textColor: "text-amber-600",
      bgLight: "bg-amber-50",
      description: "Oversight & Verification",
      isoClause: "Clause 9",
      subsections: [
        {
          id: "testing_exercises",
          name: "Testing & Exercises",
          items: [
            { name: "Exercise Calendar", href: "/exercise-calendar", icon: Calendar, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], clause: "8.5" },
            { name: "Exercise Management", href: "/exercises", icon: ClipboardCheck, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], clause: "8.5" },
          ]
        },
        {
          id: "performance_monitoring",
          name: "Performance Monitoring",
          items: [
            { name: "Performance & Compliance", href: "/performance-compliance", icon: BarChart3, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "9.1" },
            { name: "Management Review", href: "/management-review", icon: ClipboardList, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "9.3" },
          ]
        },
        {
          id: "assurance",
          name: "Assurance",
          items: [
            { name: "Internal Audit", href: "/internal-audit", icon: ClipboardList, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "9.2" },
          ]
        }
      ]
    },
    {
      id: "act",
      name: "ACT",
      icon: RefreshCw,
      color: "bg-purple-600",
      colorLight: "bg-purple-400",
      hoverColor: "hover:bg-purple-700",
      textColor: "text-purple-600",
      bgLight: "bg-purple-50",
      description: "Strategic Realignment",
      isoClause: "Clause 10",
      subsections: [
        {
          id: "continual_improvement",
          name: "Continual Improvement",
          items: [
            { name: "Lessons Learned", href: "/lessons-learned", icon: BookOpen, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "10.2" },
            { name: "Corrective & Preventive Actions", href: "/capa", icon: FileWarning, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], clause: "10.1 / 10.2" },
            { name: "PDCA Integration", href: "/pdca-integration", icon: RefreshCw, roles: ["admin", "bcm_manager"], clause: "10.2" },
          ]
        }
      ]
    },
  ],
  organization: {
    id: "organization",
    name: "ORGANIZATION",
    icon: Building2,
    color: "bg-slate-500",
    colorLight: "bg-slate-400",
    hoverColor: "hover:bg-slate-600",
    textColor: "text-slate-700",
    bgLight: "bg-slate-100",
    description: "Organizational Structure",
    isoClause: "Clause 4",
    items: [
      { name: "Org Locations & Setup", href: "/organization-setup", icon: MapPin, roles: ["admin", "bcm_manager"], badge: "AI", clause: "4.1" },
      { name: "Organization Management", href: "/org-management", icon: Building2, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], clause: "4.1" },
      { name: "Service Lines", href: "/service-line-management", icon: Layers, roles: ["admin", "bcm_manager"], clause: "4.1" },
      { name: "Connect Your Systems", href: "/federation", icon: Network, roles: ["admin", "bcm_manager", "department_head"], clause: "7.1.2" },
    ]
  },
  intelligenceAnalytics: {
    title: "Intelligence & Analytics",
    items: [
      { name: "AI Lab", href: "/ai-lab", icon: Sparkles, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "process_manager", "process_contributor", "app_manager", "app_contributor", "it_function_manager", "it_function_contributor"], badge: "AI" },
      { name: "BCM Report", href: "/reports", icon: BarChart3, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"] },
    ],
  },
  platformOperations: {
    title: "Platform Operations",
    items: [
      { name: "Audit Logs", href: "/audit-logs", icon: ScrollText, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"] },
      { name: "Scheduler", href: "/admin/scheduler", icon: Clock, roles: ["admin", "bcm_manager"] },
      { name: "AI Guide Questions", href: "/admin/pdca-questions", icon: MessageSquare, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager"], badge: "AI" },
    ],
  },
  account: {
    title: "Account",
    items: [
      { name: "Billing", href: "/billing", icon: CreditCard, roles: ["admin"] },
      { name: "Settings", href: "/settings", icon: Settings, roles: ["admin", "bcm_manager", "department_head", "it_drp_manager", "end_user", "business_user"] },
    ],
  },
};

const adminNavigation = [
  { name: "Admin Panel", href: "/admin", icon: ShieldCheck },
  { name: "Guidebook Library", href: "/admin/guidebooks", icon: BookOpen },
  { name: "Demo Snapshots", href: "/admin/snapshots", icon: Camera },
  { name: "Demo Requests", href: "/admin/demo-requests", icon: ShieldCheck, badge: "V22.9.9" },
  { name: "Trial Dashboard", href: "/trial-admin", icon: TrendingUp },
];

export default function DashboardLayout({ children }) {
  const { user, logout, api } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [recentlyVisited, setRecentlyVisited] = useState([]);
  const [aiGlobalResults, setAiGlobalResults] = useState([]);
  const [aiSearching, setAiSearching] = useState(false);
  // PDCA phase completion for the sidebar — sourced from the SAME endpoint AND
  // computation the Performance & Compliance page uses (GET /api/kpi/dashboard +
  // computeCategoryAverage). One number, one meaning, everywhere.
  const [pdcaKpiData, setPdcaKpiData] = useState(null);
  const [pdcaLoaded, setPdcaLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/kpi/dashboard");
        if (!cancelled) { setPdcaKpiData(res.data?.kpi_data || {}); setPdcaLoaded(true); }
      } catch {
        if (!cancelled) { setPdcaKpiData(null); setPdcaLoaded(true); }
      }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // Demo / sandbox users must NEVER see platform-admin tooling even though
  // they hold bcm_manager role. Detected via plan_type, org_code, org_name,
  // or email domain — any one match locks down admin surfaces.
  const isDemoUser = !!user && (
    user.plan_type === "demo" ||
    user.org_code === "DEMO01" ||
    /demo|sandbox/i.test(user.org_name || "") ||
    /@gbrms-sandbox\.com$|@meridian-demo\.com$/i.test(user.email || "")
  );
  
  const [expandedSections, setExpandedSections] = useState(() => {
    // Auto-expand the section containing the current path
    const currentPath = location.pathname;
    const expanded = {};
    pdcaNavigation.sections.forEach(section => {
      if (section.subsections) {
        const hasActiveItem = section.subsections.some(sub => 
          sub.items.some(item => item.href === currentPath)
        );
        if (hasActiveItem) {
          expanded[section.id] = true;
          // Also expand the subsection
          section.subsections.forEach(sub => {
            if (sub.items.some(item => item.href === currentPath)) {
              expanded[sub.id] = true;
            }
          });
        }
      }
    });
    return expanded;
  });

  // Keyboard shortcut for search (Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Track recently visited pages
  useEffect(() => {
    const currentPath = location.pathname;
    const currentPage = getAllMenuItems().find(item => item.href === currentPath);
    if (currentPage && currentPath !== '/dashboard') {
      setRecentlyVisited(prev => {
        const filtered = prev.filter(p => p.href !== currentPath);
        return [{ ...currentPage, visitedAt: Date.now() }, ...filtered].slice(0, 5);
      });
    }
  }, [location.pathname]);

  // Get all menu items for search
  const getAllMenuItems = useCallback(() => {
    const items = [];
    // Dashboard
    items.push({ ...pdcaNavigation.dashboard, section: 'Dashboard' });
    // Organization (flat items)
    pdcaNavigation.organization.items?.forEach(item => {
      items.push({ ...item, section: 'Organization' });
    });
    // PDCA sections
    pdcaNavigation.sections.forEach(section => {
      section.subsections?.forEach(sub => {
        sub.items.forEach(item => {
          items.push({ ...item, section: `${section.name} > ${sub.name}` });
        });
      });
    });
    // Intelligence & Analytics
    pdcaNavigation.intelligenceAnalytics.items.forEach(item => {
      items.push({ ...item, section: 'Intelligence & Analytics' });
    });
    // Platform Operations
    pdcaNavigation.platformOperations.items.forEach(item => {
      items.push({ ...item, section: 'Platform Operations' });
    });
    // Account
    pdcaNavigation.account.items.forEach(item => {
      items.push({ ...item, section: 'Account' });
    });
    return items;
  }, []);

  // Filtered search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    return getAllMenuItems()
      .filter(item => 
        item.name.toLowerCase().includes(query) || 
        item.section.toLowerCase().includes(query)
      )
      .filter(item => item.roles?.includes(user?.role))
      .filter(item => !isDemoUser || !/^\/admin(\/|$)|^\/trial-admin(\/|$)/.test(item.href || ""))
      .slice(0, 8);
  }, [searchQuery, getAllMenuItems, user?.role, isDemoUser]);

  // AI Global Cross-Module Search (debounced)
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 3) {
      setAiGlobalResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setAiSearching(true);
      const modules = ["assets", "stakeholders", "bia_processes", "risks", "recovery_plans", "documents", "threats", "lessons_learned"];
      const moduleLabels = {
        assets: "Assets", stakeholders: "Stakeholders", bia_processes: "BIA Processes",
        risks: "Risk Assessments", recovery_plans: "Recovery Plans", documents: "Documents",
        threats: "Threat Profiles", lessons_learned: "Lessons Learned",
      };
      const moduleRoutes = {
        assets: "/asset-inventory", stakeholders: "/stakeholder-register", bia_processes: "/operational-bia",
        risks: "/risk-assessment", recovery_plans: "/recovery-plans", documents: "/document-management",
        threats: "/threat-profiling", lessons_learned: "/lessons-learned",
      };
      try {
        const promises = modules.map(mod =>
          api.post("/ai-assistant/power-search", { query: searchQuery.trim(), module: mod, limit: 3 })
            .then(r => ({ module: mod, label: moduleLabels[mod], route: moduleRoutes[mod], results: r.data?.results || [], total: r.data?.total || 0 }))
            .catch(() => ({ module: mod, label: moduleLabels[mod], route: moduleRoutes[mod], results: [], total: 0 }))
        );
        const allResults = await Promise.all(promises);
        setAiGlobalResults(allResults.filter(r => r.total > 0));
      } catch (e) {
        console.error("Global AI search failed:", e);
      } finally {
        setAiSearching(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [searchQuery, api]);

  // Update expanded section when route changes
  useEffect(() => {
    const currentPath = location.pathname;
    pdcaNavigation.sections.forEach(section => {
      if (section.subsections) {
        const hasActiveItem = section.subsections.some(sub => 
          sub.items.some(item => item.href === currentPath)
        );
        if (hasActiveItem && !expandedSections[section.id]) {
          setExpandedSections(prev => {
            const newState = { ...prev, [section.id]: true };
            // Also expand the subsection
            section.subsections.forEach(sub => {
              if (sub.items.some(item => item.href === currentPath)) {
                newState[sub.id] = true;
              }
            });
            return newState;
          });
        }
      }
    });
  }, [location.pathname]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "admin":
        return { text: "Website Admin", color: "bg-purple-100 text-purple-700" };
      case "bcm_manager":
        return { text: "BCM Manager", color: "bg-blue-100 text-blue-700" };
      case "it_drp_manager":
        return { text: "IT-DRP Manager", color: "bg-cyan-100 text-cyan-700" };
      case "process_manager":
        return { text: "Process Manager", color: "bg-emerald-100 text-emerald-700" };
      case "process_contributor":
        return { text: "Process Contributor", color: "bg-slate-100 text-slate-700" };
      case "app_manager":
        return { text: "App Manager", color: "bg-sky-100 text-sky-700" };
      case "app_contributor":
        return { text: "App Contributor", color: "bg-slate-100 text-slate-700" };
      case "it_function_manager":
        return { text: "IT Function Manager", color: "bg-sky-100 text-sky-700" };
      case "it_function_contributor":
        return { text: "IT Function Contributor", color: "bg-slate-100 text-slate-700" };
      case "end_user":
      case "business_user":
        return { text: "Business User", color: "bg-emerald-100 text-emerald-700" };
      default:
        return { text: role?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'User', color: "bg-slate-100 text-slate-700" };
    }
  };

  const roleBadge = getRoleBadge(user?.role);
  // Human-readable role label derived from the STORED role (user.role) — the
  // SAME source the sidebar/permission checks use — so the header can never
  // claim a role (e.g. "BCM Manager" via a stale invite-time role_label) that
  // the backend does not actually grant.
  const roleDisplayLabel = formatRoleLabel(user?.role);

  // PDCA phase completion — identical to the Performance & Compliance KPI tab:
  // computeCategoryAverage over /api/kpi/dashboard data. Returns null (rendered
  // "—") only while loading or on fetch error; once loaded returns the same
  // 0-100 figure the P&C page shows (0% for an unmeasured category, matching it
  // digit-for-digit). Never a hardcoded constant, never NaN.
  const getSectionProgress = (sectionId) => {
    if (!pdcaLoaded || pdcaKpiData === null) return null;
    return computeCategoryAverage(pdcaKpiData, sectionId);
  };
  // Coverage (measured / total KPIs) for the small "1/4" beside each phase %.
  const getSectionCoverage = (sectionId) => {
    if (!pdcaLoaded || pdcaKpiData === null) return null;
    return computeCategoryCoverage(pdcaKpiData, sectionId);
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo & Collapse Toggle */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200">
        <Link to="/dashboard" className="flex items-center gap-3">
          <div className="w-[60px] h-[60px] rounded-lg overflow-hidden flex items-center justify-center bg-white shadow-md">
            <img
              src="https://customer-assets.emergentagent.com/job_cc74fbba-7d42-4e61-a0ea-eedd771fa683/artifacts/7xq5yifk_ResHubNEW1.1-removebg-preview.png"
              alt="ResiliencyHUB"
              className="w-[60px] h-[60px] object-contain"
            />
          </div>
          {!sidebarCollapsed && (
            <div>
              <BrandText size="lg" className="block leading-tight" />
              <p className="text-[12px] font-medium mt-0.5" style={{ color: '#0e6f8e' }}>AI-Powered Resiliency Management Service</p>
            </div>
          )}
        </Link>
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="p-1.5 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors hidden lg:flex"
        >
          {sidebarCollapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>
      </div>

      {/* Quick Search Trigger */}
      {!sidebarCollapsed && (
        <div className="px-3 py-3">
          <button
            onClick={() => setSearchOpen(true)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-200 transition-all text-sm"
          >
            <Search className="w-4 h-4" />
            <span className="flex-1 text-left">Quick search...</span>
            <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono bg-slate-200 rounded text-slate-500">
              <Command className="w-3 h-3" />K
            </kbd>
          </button>
        </div>
      )}

      {/* Recently Visited */}
      {!sidebarCollapsed && recentlyVisited.length > 0 && (
        <div className="px-3 pb-2">
          <div className="flex items-center gap-2 px-2 mb-2">
            <Clock className="w-3 h-3 text-slate-500" />
            <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Recent</span>
          </div>
          <div className="space-y-0.5">
            {recentlyVisited.slice(0, 3).map((item) => {
              const ItemIcon = item.icon;
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-all"
                >
                  <ItemIcon className="w-3 h-3" />
                  <span className="truncate">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto min-h-0 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-transparent">
        {/* Dashboard Link */}
        {pdcaNavigation.dashboard.roles.includes(user?.role) && (
          <Link
            to={pdcaNavigation.dashboard.href}
            className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
              location.pathname === pdcaNavigation.dashboard.href 
                ? "bg-sky-50 text-sky-700 font-semibold" 
                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            }`}
            data-testid="nav-dashboard"
          >
            {location.pathname === pdcaNavigation.dashboard.href && (
              <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-sky-600 rounded-r-full" />
            )}
            <LayoutDashboard className="w-5 h-5" />
            {!sidebarCollapsed && <span className="font-medium">BCM Dashboard</span>}
          </Link>
        )}

        {/* AI Lab moved into Intelligence & Analytics section (Taxonomy v2) */}

        {/* Organization Section - PDCA Style */}
        {!sidebarCollapsed && (
          <div className="pt-3 pb-2">
            {(() => {
              const orgSection = pdcaNavigation.organization;
              // Task 3: Use direct items instead of subsections
              const visibleItems = orgSection.items?.filter(item => item.roles.includes(user?.role)) || [];
              
              if (visibleItems.length === 0) return null;
              
              const isExpanded = expandedSections['organization'];
              const hasActiveItem = visibleItems.some(item => location.pathname === item.href);
              const SectionIcon = orgSection.icon;
              
              return (
                <div className="space-y-1">
                  {/* Organization Header Button */}
                  <button
                    onClick={() => toggleSection('organization')}
                    className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                      isExpanded || hasActiveItem
                        ? `bg-gradient-to-r ${orgSection.bgLight} border border-slate-200`
                        : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                    }`}
                    data-testid="nav-organization"
                  >
                    <div className={`p-1.5 rounded-md shadow-sm ${isExpanded || hasActiveItem ? orgSection.color : (orgSection.colorLight || "bg-slate-400")} transition-all`}>
                      <SectionIcon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold text-sm ${isExpanded || hasActiveItem ? orgSection.textColor : ""}`}>
                          {orgSection.name}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-500 mt-0.5">{orgSection.description}</p>
                    </div>
                    <ChevronDown 
                      className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""} ${
                        isExpanded || hasActiveItem ? orgSection.textColor : "text-slate-500"
                      }`} 
                    />
                  </button>
                  
                  {/* Organization Items - Direct without subsection headers (Task 3) */}
                  {isExpanded && (
                    <div className="pl-3 space-y-0.5 animate-in slide-in-from-top-2 duration-200">
                      {visibleItems.map((item) => {
                        const ItemIcon = item.icon;
                        const isActive = location.pathname === item.href;
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`group relative flex items-center gap-2.5 px-3 py-2 ml-2 rounded-md text-sm transition-all duration-200 ${
                              isActive 
                                ? `${orgSection.bgLight} ${orgSection.textColor} font-medium border-l-2 border-current` 
                                : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                            }`}
                            data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <ItemIcon className="w-4 h-4" />
                            <span className="flex-1">{item.name}</span>
                            {item.badge && (
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${getBadgeStyle(item.badge)}`}>
                                {item.badge}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* PDCA Sections */}
        <div className="pt-3 space-y-1.5">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg border-l-2 border-sky-600">
            <RefreshCw className="w-4 h-4 text-sky-600" />
            <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">PDCA Cycle</span>
          </div>
          
          {pdcaNavigation.sections.map((section) => {
            // Filter subsections and items based on user role
            const visibleSubsections = section.subsections?.map(sub => ({
              ...sub,
              items: sub.items.filter(item => item.roles.includes(user?.role))
            })).filter(sub => sub.items.length > 0) || [];
            
            if (visibleSubsections.length === 0) return null;
            
            const isExpanded = expandedSections[section.id];
            const hasActiveItem = visibleSubsections.some(sub => 
              sub.items.some(item => location.pathname === item.href)
            );
            const SectionIcon = section.icon;
            const progress = getSectionProgress(section.id);
            const coverage = getSectionCoverage(section.id);
            
            return (
              <div key={section.id} className="space-y-1">
                {/* Section Header Button - Enhanced */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                    isExpanded || hasActiveItem
                      ? `bg-gradient-to-r ${section.bgLight} border border-${section.textColor.replace('text-', '')}/20`
                      : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  }`}
                  data-testid={`pdca-${section.id}`}
                >
                  <div className={`p-1.5 rounded-md shadow-sm ${isExpanded || hasActiveItem ? section.color : (section.colorLight || "bg-slate-300")} transition-all`}>
                    <SectionIcon className="w-4 h-4 text-white" />
                  </div>
                  {!sidebarCollapsed && (
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold text-sm ${isExpanded || hasActiveItem ? section.textColor : ""}`}>
                          {section.name}
                        </span>
                        {/* Progress indicator - Enhanced */}
                        <div className="flex-1 h-1.5 bg-slate-200/50 rounded-full overflow-hidden max-w-[50px] ml-auto">
                          <div 
                            className={`h-full ${section.color} transition-all duration-500 rounded-full`}
                            style={{ width: `${progress === null ? 0 : progress}%` }}
                          />
                        </div>
                        <span className={`text-[10px] font-medium ${section.textColor}`}>
                          {progress === null ? "—" : `${progress}%`}
                        </span>
                        {coverage && (
                          <span className="text-[9px] font-medium text-slate-400" title={`${coverage.measured} of ${coverage.total} measures`} data-testid={`pdca-coverage-${section.id}`}>
                            {coverage.measured}/{coverage.total}
                          </span>
                        )}
                      </div>
                      <p className={`text-[10px] mt-0.5 line-clamp-1 ${isExpanded || hasActiveItem ? "text-slate-600" : "text-slate-500"}`}>
                        {section.description}
                      </p>
                    </div>
                  )}
                  {!sidebarCollapsed && (
                    <ChevronRight className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? "rotate-90" : ""}`} />
                  )}
                </button>
                
                {/* Expanded Subsections - Enhanced Animation */}
                {!sidebarCollapsed && (
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"}`}>
                    <div className="pl-4 space-y-1 pt-1 border-l-2 border-slate-200 ml-5">
                      {visibleSubsections.map((subsection, subIdx) => {
                        const isSubExpanded = expandedSections[subsection.id];
                        const hasSubActiveItem = subsection.items.some(item => location.pathname === item.href);
                        
                        return (
                          <div 
                            key={subsection.id} 
                            className="space-y-0.5"
                            style={{ animationDelay: `${subIdx * 50}ms` }}
                          >
                            {/* Subsection Header - Enhanced */}
                            <button
                              onClick={() => toggleSection(subsection.id)}
                              className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-all duration-200 ${
                                isSubExpanded || hasSubActiveItem
                                  ? `bg-slate-100 text-slate-700 ${section.textColor.replace('text-', 'border-l-2 border-')}`
                                  : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                              }`}
                              data-testid={`subsection-${subsection.id}`}
                            >
                              <ChevronRight className={`w-3 h-3 transition-transform duration-200 ${isSubExpanded ? "rotate-90" : ""}`} />
                              <span className="font-medium">{subsection.name}</span>
                              <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${
                                isSubExpanded || hasSubActiveItem ? `${section.bgLight} ${section.textColor}` : `${section.bgLight} ${section.textColor} opacity-70`
                              }`}>
                                {subsection.items.length}
                              </span>
                            </button>
                          
                          {/* Subsection Items - Enhanced */}
                          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isSubExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
                            <div className="pl-4 space-y-0.5 pt-1">
                              {subsection.items.map((item, itemIdx) => {
                                const ItemIcon = item.icon;
                                const isActive = location.pathname === item.href;
                                
                                return (
                                  <Link
                                    key={item.name}
                                    to={item.href}
                                    className={`group relative flex items-center gap-2 px-3 py-1.5 rounded-md text-xs transition-all duration-200 ${
                                      isActive
                                        ? `${section.bgLight} ${section.textColor} font-medium shadow-sm`
                                        : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                                    }`}
                                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                                    style={{ animationDelay: `${itemIdx * 30}ms` }}
                                  >
                                    {isActive && (
                                      <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-4 ${section.color} rounded-r-full`} />
                                    )}
                                    <ItemIcon className={`w-3.5 h-3.5 transition-transform group-hover:scale-110 ${isActive ? section.textColor : ""}`} />
                                    <span className="flex-1 truncate">{item.name}</span>
                                    {user?.plan_type === "demo" && WIZARD_STEP_MAP[item.href] && (
                                      <span className="text-[7px] px-1 py-0.5 rounded bg-sky-100 text-sky-600 font-bold whitespace-nowrap animate-pulse" style={{ animationDuration: "3s" }}>
                                        {WIZARD_STEP_MAP[item.href]}-step
                                      </span>
                                    )}
                                    {item.badge && (
                                      <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-semibold ${getBadgeStyle(item.badge)}`}>
                                        {item.badge}
                                      </span>
                                    )}
                                  </Link>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              </div>
            );
          })}
        </div>

        {/* Intelligence & Analytics Section (was Reports & Intelligence) */}
        {!sidebarCollapsed && pdcaNavigation.intelligenceAnalytics.items.some(item => item.roles.includes(user?.role)) && (
          <div className="pt-4">
            <div className="flex items-center gap-2 px-3 py-2 mb-2 bg-slate-100 rounded-lg border-l-2 border-amber-500">
              <BarChart3 className="w-4 h-4 text-amber-600" />
              <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                {pdcaNavigation.intelligenceAnalytics.title}
              </span>
            </div>
            <div className="space-y-0.5 pl-1">
              {pdcaNavigation.intelligenceAnalytics.items.map((item) => {
                if (!item.roles.includes(user?.role)) return null;
                const ItemIcon = item.icon;
                const isActive = location.pathname === item.href;

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group relative flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-all duration-200 ${
                      isActive
                        ? "bg-amber-600/20 text-amber-300 font-medium"
                        : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                    }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-amber-500 rounded-r-full" />
                    )}
                    <ItemIcon className="w-4 h-4" />
                    <span>{item.name}</span>
                    {item.badge && (
                      <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${getBadgeStyle(item.badge)}`}>
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Platform Operations Section (NEW in Taxonomy v2) — hidden for demo users */}
        {!sidebarCollapsed && !isDemoUser && pdcaNavigation.platformOperations.items.some(item => item.roles.includes(user?.role)) && (
          <div className="pt-4">
            <div className="flex items-center gap-2 px-3 py-2 mb-2 bg-slate-100 rounded-lg border-l-2 border-teal-500">
              <Database className="w-4 h-4 text-teal-600" />
              <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                {pdcaNavigation.platformOperations.title}
              </span>
            </div>
            <div className="space-y-0.5 pl-1">
              {pdcaNavigation.platformOperations.items.map((item) => {
                if (!item.roles.includes(user?.role)) return null;
                const ItemIcon = item.icon;
                const isActive = location.pathname === item.href;

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group relative flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-all duration-200 ${
                      isActive
                        ? "bg-teal-600/20 text-teal-700 font-medium"
                        : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                    }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-teal-500 rounded-r-full" />
                    )}
                    <ItemIcon className="w-4 h-4" />
                    <span>{item.name}</span>
                    {item.badge && (
                      <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${getBadgeStyle(item.badge)}`}>
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Account Section - Enhanced */}
        {!sidebarCollapsed && (
          <div className="pt-4">
            <div className="flex items-center gap-2 px-3 py-2 mb-2 bg-slate-100 rounded-lg border-l-2 border-slate-400">
              <Settings className="w-4 h-4 text-slate-500" />
              <span className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">
                {pdcaNavigation.account.title}
              </span>
            </div>
            <div className="space-y-0.5 pl-1">
              {pdcaNavigation.account.items.map((item) => {
                if (!item.roles.includes(user?.role)) return null;
                const ItemIcon = item.icon;
                const isActive = location.pathname === item.href;
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group relative flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-all duration-200 ${
                      isActive 
                        ? "bg-slate-700/50 text-slate-200 font-medium" 
                        : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                    }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-slate-400 rounded-r-full" />
                    )}
                    <ItemIcon className="w-4 h-4" />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
              {/* Guidebook Index — WebAdmin only (internal-only material). Non-admin roles must not see or reach it. */}
              {user?.role === "admin" && (
                <a
                  href="/ResiliencyHUB_Guidebook_Index.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-all duration-200 text-slate-500 hover:bg-orange-50 hover:text-orange-700"
                  data-testid="nav-guidebook-index"
                >
                  <Library className="w-4 h-4" />
                  <span>Guidebook Index</span>
                </a>
              )}
            </div>
          </div>
        )}
        {user?.role === "admin" && !isDemoUser && !sidebarCollapsed && (
          <div className="pt-4">
            <div className="flex items-center gap-2 px-3 py-2 mb-2 bg-amber-50 rounded-lg border-l-2 border-amber-500">
              <ShieldCheck className="w-4 h-4 text-amber-600" />
              <span className="text-[11px] font-bold text-amber-700 uppercase tracking-wider">
                Administration
              </span>
            </div>
            <div className="space-y-0.5 pl-1">
              {adminNavigation
                .filter((item) => item.href !== "/trial-admin" || user?.role === "admin")
                .map((item) => {
                const ItemIcon = item.icon;
                const isActive = location.pathname === item.href;
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group relative flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-all duration-200 ${
                      isActive 
                        ? "bg-amber-600/20 text-amber-300 font-medium" 
                        : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                    }`}
                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-amber-500 rounded-r-full" />
                    )}
                    <ItemIcon className="w-4 h-4" />
                    <span>{item.name}</span>
                    {item.badge && (
                      <span className={`ml-auto text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${getBadgeStyle(item.badge)}`}>
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Guide Link - Enhanced — WebAdmin (role=admin) only */}
        {!sidebarCollapsed && user?.role === "admin" && (
          <div className="pt-4 pb-2">
            <Link
              to="/guide"
              className={`group relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all duration-200 ${
                location.pathname === "/guide" 
                  ? "bg-gradient-to-r from-emerald-600/20 to-teal-600/20 text-emerald-300 font-medium" 
                  : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"
              }`}
              data-testid="nav-guide"
            >
              {location.pathname === "/guide" && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-gradient-to-b from-emerald-500 to-teal-500 rounded-r-full" />
              )}
              <BookOpen className="w-5 h-5" />
              <span>How to Use ResiliencyHUB</span>
            </Link>
          </div>
        )}
      </nav>

      {/* Replay Demo Tour - Demo users only */}
      {/* Mandate 5 — Demo Guide removed. Demo users keep only "Explore
          Live Demo" (the /try-demo landing) + the Replay Quick Start
          walkthrough, which now carries the interactive tooltips + AI
          guidance (Mandate 5 refinement). */}
      {user?.plan_type === "demo" && !sidebarCollapsed && (
        <div className="px-4 pt-3 space-y-2">
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent("replay-demo-tour"));
            }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-sky-50 to-teal-50 border border-sky-200 text-sky-700 hover:from-sky-100 hover:to-teal-100 hover:shadow-sm transition-all text-xs font-medium group"
            data-testid="replay-demo-tour-btn"
          >
            <PlayCircle className="w-4 h-4 group-hover:scale-110 transition-transform" />
            <span className="flex-1 text-left">Explore Live Demo</span>
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
          </button>
        </div>
      )}

      {/* User Section — mobile sidebar only. On desktop the profile lives in the
          top-right header (UserProfileMenu); hiding it here keeps the desktop
          sidebar ending cleanly with no empty gap. */}
      <div className={`lg:hidden p-4 border-t border-slate-200 bg-slate-50 ${sidebarCollapsed ? 'flex justify-center' : ''}`}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className={`flex items-center gap-3 p-2 rounded-lg hover:bg-slate-100 transition-all duration-200 ${sidebarCollapsed ? 'justify-center' : 'w-full'}`}>
              <div className="w-9 h-9 rounded-full bg-sky-700 flex items-center justify-center shadow-md">
                <User className="w-5 h-5 text-white" />
              </div>
              {!sidebarCollapsed && (
                <>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                    <span
                      data-testid="sidebar-role-badge"
                      className={`inline-block mt-1 px-2 py-0.5 rounded text-[11px] font-medium ${roleBadge.color}`}
                    >
                      {roleDisplayLabel}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
              <p className="text-xs text-slate-500">{user?.email}</p>
              <span
                data-testid="menu-role-badge"
                className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${roleBadge.color}`}
              >
                {roleDisplayLabel}
              </span>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings" className="cursor-pointer">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/dashboard" className="cursor-pointer">
                <Home className="w-4 h-4 mr-2" />
                Home
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  // App-wide user profile widget for the top-right header (Section E). Same
  // info + dropdown behaviour that used to live at the bottom of the sidebar.
  const UserProfileMenu = () => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="topbar-profile-trigger" className="flex items-center gap-2 p-1 rounded-lg hover:bg-slate-100 transition-colors">
          <div className="w-8 h-8 rounded-full bg-sky-700 flex items-center justify-center shadow-sm shrink-0">
            <User className="w-4 h-4 text-white" />
          </div>
          <div className="hidden xl:block text-left min-w-0 max-w-[160px]">
            <p className="text-sm font-medium text-slate-900 truncate leading-tight">
              {user?.name || `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email}
            </p>
            <p className="text-[11px] text-slate-500 truncate leading-tight">{user?.email}</p>
          </div>
          <span data-testid="topbar-role-badge" className={`hidden sm:inline-block px-2 py-0.5 rounded text-[11px] font-medium ${roleBadge.color}`}>
            {roleDisplayLabel}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
          <p className="text-xs text-slate-500">{user?.email}</p>
          <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${roleBadge.color}`}>
            {roleDisplayLabel}
          </span>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings" className="cursor-pointer"><Settings className="w-4 h-4 mr-2" />Settings</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/dashboard" className="cursor-pointer"><Home className="w-4 h-4 mr-2" />Home</Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
          <LogOut className="w-4 h-4 mr-2" />Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="min-h-screen bg-slate-100" data-testid="dashboard-layout">
      {/* Trial Banner — padded to clear the fixed sidebar so its content isn't hidden */}
      <div className={`transition-all duration-300 pt-16 lg:pt-0 ${sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-72'}`}>
        <TrialBanner />
      </div>
      {/* Trial Soft Wall Modal */}
      <TrialSoftWall />
      {/* Onboarding Checklist */}
      <OnboardingChecklist />
      {/* Global AI Power Search — Cmd+Shift+K for cross-collection natural-language queries */}
      <GlobalAISearch />
      {/* Command Palette (Cmd+K) */}
      <CommandDialog open={searchOpen} onOpenChange={(open) => { setSearchOpen(open); if (!open) { setSearchQuery(""); setAiGlobalResults([]); } }}>
        <CommandInput
          placeholder="Search pages, data across all modules, or type a natural language query..."
          value={searchQuery}
          onValueChange={setSearchQuery}
        />
        <CommandList className="max-h-[420px]">
          <CommandEmpty>
            <div className="py-4 text-center">
              {aiSearching ? (
                <>
                  <Loader2 className="w-8 h-8 mx-auto mb-2 text-sky-400 animate-spin" />
                  <p className="text-sm text-sky-600">Searching across all modules...</p>
                </>
              ) : (
                <>
                  <FileSearch className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm text-slate-500">No pages match "{searchQuery}"</p>
                  {searchQuery.trim().length < 3 && (
                    <p className="text-xs text-slate-400 mt-1">Type 3+ characters for AI cross-module search</p>
                  )}
                  {searchQuery.trim().length >= 3 && aiGlobalResults.length === 0 && !aiSearching && (
                    <p className="text-xs text-slate-400 mt-1">No data found across modules either</p>
                  )}
                </>
              )}
            </div>
          </CommandEmpty>

          {/* Recently Visited */}
          {searchQuery.trim() === "" && recentlyVisited.length > 0 && (
            <CommandGroup heading="Recently Visited">
              {recentlyVisited.map((item) => {
                const ItemIcon = item.icon || FileSearch;
                return (
                  <CommandItem
                    key={`recent-${item.href}`}
                    value={`recent ${item.name} ${item.section || ""}`}
                    onSelect={() => { navigate(item.href); setSearchOpen(false); setSearchQuery(""); }}
                    className="flex items-center gap-3 px-2 py-2.5 cursor-pointer"
                  >
                    <div className="p-1.5 rounded-md bg-sky-50">
                      <ItemIcon className="w-4 h-4 text-sky-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.name}</p>
                      {item.section && <p className="text-[10px] text-slate-400 truncate">{item.section}</p>}
                    </div>
                    <Clock className="w-3.5 h-3.5 text-slate-300" />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          )}

          {/* Quick Actions */}
          {searchQuery.trim() === "" && (
            <CommandGroup heading="Quick Actions">
              <CommandItem
                value="create bia process"
                onSelect={() => { navigate("/operational-bia"); setSearchOpen(false); }}
                className="flex items-center gap-3 px-2 py-2.5 cursor-pointer"
              >
                <div className="p-1.5 rounded-md bg-emerald-50">
                  <Plus className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex-1"><p className="text-sm font-medium">New BIA Process</p><p className="text-[10px] text-slate-400">Create a new Business Impact Analysis</p></div>
                <CommandShortcut>BIA</CommandShortcut>
              </CommandItem>
              <CommandItem
                value="generate recovery plan"
                onSelect={() => { navigate("/recovery-plans"); setSearchOpen(false); }}
                className="flex items-center gap-3 px-2 py-2.5 cursor-pointer"
              >
                <div className="p-1.5 rounded-md bg-blue-50">
                  <FileCheck className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1"><p className="text-sm font-medium">Generate Recovery Plan</p><p className="text-[10px] text-slate-400">Create a new BCP or IT-DRP plan</p></div>
                <CommandShortcut>BCP</CommandShortcut>
              </CommandItem>
              <CommandItem
                value="view bcm reports"
                onSelect={() => { navigate("/reports"); setSearchOpen(false); }}
                className="flex items-center gap-3 px-2 py-2.5 cursor-pointer"
              >
                <div className="p-1.5 rounded-md bg-amber-50">
                  <BarChart3 className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1"><p className="text-sm font-medium">View BCM Reports</p><p className="text-[10px] text-slate-400">Executive reports with AI insights</p></div>
                <CommandShortcut>RPT</CommandShortcut>
              </CommandItem>
              <CommandItem
                value="check compliance"
                onSelect={() => { navigate("/performance-compliance?tab=compliance"); setSearchOpen(false); }}
                className="flex items-center gap-3 px-2 py-2.5 cursor-pointer"
              >
                <div className="p-1.5 rounded-md bg-purple-50">
                  <Shield className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1"><p className="text-sm font-medium">Check Compliance</p><p className="text-[10px] text-slate-400">ISO 22301 compliance status</p></div>
                <CommandShortcut>ISO</CommandShortcut>
              </CommandItem>
            </CommandGroup>
          )}

          {/* Search Results — Grouped by PDCA Phase */}
          {searchQuery.trim() !== "" && (() => {
            const groups = {};
            searchResults.forEach((item) => {
              const group = item.section?.split(" > ")[0] || "Other";
              if (!groups[group]) groups[group] = [];
              groups[group].push(item);
            });
            return Object.entries(groups).map(([group, items]) => (
              <CommandGroup key={group} heading={group}>
                {items.map((item) => {
                  const ItemIcon = item.icon || FileSearch;
                  return (
                    <CommandItem
                      key={item.href}
                      value={`${item.name} ${item.section || ""}`}
                      onSelect={() => { navigate(item.href); setSearchOpen(false); setSearchQuery(""); }}
                      className="flex items-center gap-3 px-2 py-2.5 cursor-pointer"
                    >
                      <div className="p-1.5 rounded-md bg-slate-100">
                        <ItemIcon className="w-4 h-4 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.name}</p>
                        <p className="text-[10px] text-slate-400 truncate">{item.section}</p>
                      </div>
                      {item.clause && <span className="text-[9px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{item.clause}</span>}
                      <ChevronRight className="w-3.5 h-3.5 text-slate-300" />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ));
          })()}

          {/* AI Global Cross-Module Results */}
          {searchQuery.trim().length >= 3 && (aiSearching || aiGlobalResults.length > 0) && (
            <>
              {aiSearching && (
                <div className="flex items-center gap-2 px-4 py-3 text-xs text-sky-600">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <Sparkles className="w-3.5 h-3.5" />
                  Searching across all modules...
                </div>
              )}
              {aiGlobalResults.map(moduleResult => (
                <CommandGroup key={moduleResult.module} heading={`${moduleResult.label} (${moduleResult.total} found)`}>
                  {moduleResult.results.slice(0, 3).map((item, idx) => {
                    const displayName = item.name || item.asset_name || item.process_name || item.title || item.threat_name || item.application_name || item.trainee_name || "Unnamed";
                    const subText = item.department || item.asset_type || item.category || item.status || item.business_unit || item.source_type || "";
                    return (
                      <CommandItem
                        key={`${moduleResult.module}-${idx}`}
                        value={`ai ${moduleResult.module} ${displayName} ${subText}`}
                        onSelect={() => { navigate(moduleResult.route); setSearchOpen(false); setSearchQuery(""); }}
                        className="flex items-center gap-3 px-2 py-2.5 cursor-pointer"
                      >
                        <div className="p-1.5 rounded-md bg-sky-50">
                          <Database className="w-4 h-4 text-sky-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{displayName}</p>
                          <p className="text-[10px] text-slate-400 truncate">{subText}</p>
                        </div>
                        <span className="text-[9px] text-sky-500 bg-sky-50 px-1.5 py-0.5 rounded whitespace-nowrap">{moduleResult.label}</span>
                      </CommandItem>
                    );
                  })}
                  {moduleResult.total > 3 && (
                    <CommandItem
                      value={`ai-more-${moduleResult.module}`}
                      onSelect={() => { navigate(moduleResult.route); setSearchOpen(false); setSearchQuery(""); }}
                      className="flex items-center gap-3 px-2 py-2 cursor-pointer text-sky-600"
                    >
                      <span className="text-xs font-medium ml-9">View all {moduleResult.total} results in {moduleResult.label} →</span>
                    </CommandItem>
                  )}
                </CommandGroup>
              ))}
            </>
          )}
        </CommandList>
        <div className="p-2.5 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-100 rounded font-mono text-[9px]">↑↓</kbd> navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-100 rounded font-mono text-[9px]">↵</kbd> select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 bg-slate-100 rounded font-mono text-[9px]">esc</kbd> close
            </span>
          </div>
          <span className="flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-sky-500" />
            <span className="text-sky-500">AI-powered</span>
            <kbd className="px-1.5 py-0.5 bg-slate-100 rounded font-mono text-[9px]">⌘K</kbd>
          </span>
        </div>
      </CommandDialog>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-3">
          <div className="w-[44px] h-[44px] rounded-lg overflow-hidden flex items-center justify-center bg-white">
            <img
              src="https://customer-assets.emergentagent.com/job_cc74fbba-7d42-4e61-a0ea-eedd771fa683/artifacts/7xq5yifk_ResHubNEW1.1-removebg-preview.png"
              alt="ResiliencyHUB"
              className="w-[44px] h-[44px] object-contain"
            />
          </div>
          <div>
            <BrandText size="md" className="block leading-tight" />
            <p className="text-[11px] font-medium mt-0.5" style={{ color: '#0e6f8e' }}>AI-Powered Resiliency Management Service</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSearchOpen(true)}
            className="p-2 text-slate-500 hover:text-slate-900"
          >
            <Search className="w-5 h-5" />
          </button>
          <AIDraftsBell />
          <NotificationBell />
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-slate-500 hover:text-slate-900"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Desktop Sidebar - With Collapse Support */}
      <aside className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 bg-white border-r border-slate-200 transition-all duration-300 ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-72'}`}>
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 w-72 bg-white border-r border-slate-200 z-50 transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <SidebarContent />
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content - Adjusts for collapsed sidebar */}
      <main className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-72'}`}>
        {/* Desktop Top Bar with Notification Bell */}
        <div className="hidden lg:flex items-center justify-between px-6 py-3 bg-white border-b">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:text-slate-700 hover:border-slate-300 transition-colors text-sm"
            >
              <Search className="w-4 h-4" />
              <span>Search...</span>
              <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono bg-slate-100 rounded text-slate-400">
                <Command className="w-3 h-3" />K
              </kbd>
            </button>
          </div>
          <div className="flex items-center gap-4">
            <AIDraftsBell />
            <NotificationBell />
            <UserProfileMenu />
          </div>
        </div>
        <ComplianceBanner />
        {/* ISO 22301 Clause Reference Banner */}
        {(() => {
          const clauseInfo = getPageClauseInfo(location.pathname);
          if (!clauseInfo) return null;
          const phaseColors = {
            blue: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", badge: "bg-blue-100 text-blue-700", label: "PLAN" },
            emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", badge: "bg-emerald-100 text-emerald-700", label: "DO" },
            amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", badge: "bg-amber-100 text-amber-700", label: "CHECK" },
            purple: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-200", badge: "bg-purple-100 text-purple-700", label: "ACT" },
            slate: { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200", badge: "bg-slate-100 text-slate-700", label: "" },
          };
          const style = phaseColors[clauseInfo.phaseColor] || phaseColors.slate;
          return (
            <div className={`flex items-center gap-3 px-6 py-2.5 ${style.bg} border-b ${style.border}`}>
              {style.label && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${style.badge} uppercase tracking-wider`}>
                  {style.label}
                </span>
              )}
              <span className={`text-xs font-semibold ${style.text}`}>
                ISO 22301:2019 — Clause {clauseInfo.clause}
              </span>
              {clauseInfo.description && (
                <span className="text-xs text-slate-500 hidden sm:inline">
                  {clauseInfo.description}
                </span>
              )}
            </div>
          );
        })()}
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
      {/* Demo Checklist Overlay — mounted globally to track navigation */}
      <DemoWelcomeOverlay />
      {/* Guided Workflow Panel — persistent floating progress indicator + slide-out */}
      <GuidedWorkflowPanel />
    </div>
  );
}
